import { Editor } from "@monaco-editor/react";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import CloseIcon from "@mui/icons-material/Close";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import type React from "react";
import { useCallback, useDeferredValue, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Virtuoso } from "react-virtuoso";
import { EmptyState, LoadingBox } from "@/components/ui";
import { STATUS_COLORS } from "@/theme/theme";
import { useAnalyzeHar } from "../hooks/use-suggested-checks";
import { clickableProps } from "../utils/keyboard";
import { CreateCheckFromHarPanel } from "./checks/create-check-from-har-panel";

// HAR format interfaces - exported for use in other components
export interface HarHeader {
  name: string;
  value: string;
}

export interface HarRequest {
  method: string;
  url: string;
  httpVersion: string;
  headers: HarHeader[];
  queryString: Array<{ name: string; value: string }>;
  cookies: Array<{ name: string; value: string }>;
  headersSize: number;
  bodySize: number;
  postData?: {
    mimeType: string;
    text?: string;
    params?: Array<{ name: string; value: string }>;
  };
}

export interface HarResponse {
  status: number;
  statusText: string;
  httpVersion: string;
  headers: HarHeader[];
  cookies: Array<{ name: string; value: string }>;
  content: {
    size: number;
    mimeType: string;
    text?: string;
    compression?: number;
  };
  headersSize: number;
  bodySize: number;
  redirectURL: string;
  _transferSize?: number;
}

export interface HarTimings {
  dns: number;
  connect: number;
  ssl?: number;
  send: number;
  wait: number;
  receive: number;
}

export interface HarEntry {
  startedDateTime: string;
  time: number;
  request: HarRequest;
  response: HarResponse;
  cache: object;
  timings: HarTimings;
  pageref?: string;
  serverIPAddress?: string;
  _serverPort?: number;
}

interface HarLog {
  version: string;
  creator: {
    name: string;
    version: string;
  };
  browser?: {
    name: string;
    version: string;
  };
  pages?: Array<{
    startedDateTime: string;
    id: string;
    title: string;
    pageTimings: {
      onContentLoad?: number;
      onLoad?: number;
    };
  }>;
  entries: HarEntry[];
}

interface HarData {
  log: HarLog;
}

interface HarViewerProps {
  harPath: string;
  testId?: string;
  testRunId?: string;
  appConfigId?: string;
  showExpandButton?: boolean;
}

interface DetailsPanelProps {
  entry: HarEntry | null;
  open: boolean;
  onClose: () => void;
  testId?: string;
  testRunId?: string;
  appConfigId?: string;
}

type RequestType =
  | "all"
  | "document"
  | "script"
  | "stylesheet"
  | "image"
  | "xhr"
  | "font"
  | "media"
  | "other";

// Pre-computed entry data for performance
interface ProcessedEntry {
  entry: HarEntry;
  index: number;
  timestamp: number;
  urlPath: string;
  urlSearch: string;
  fileName: string;
  leftPercent: number;
  widthPercent: number;
  tooltipText: string;
}

const getMethodColor = (
  method: string,
): "primary" | "secondary" | "success" | "warning" | "error" => {
  switch (method.toUpperCase()) {
    case "GET":
      return "primary";
    case "POST":
      return "success";
    case "PUT":
      return "warning";
    case "DELETE":
      return "error";
    default:
      return "secondary";
  }
};

const getStatusColor = (
  status: number,
): "primary" | "secondary" | "success" | "warning" | "error" => {
  if (status >= 200 && status < 300) return "success";
  if (status >= 300 && status < 400) return "warning";
  if (status >= 400) return "error";
  return "secondary";
};

const getRequestType = (entry: HarEntry): string => {
  const url = entry.request.url.toLowerCase();
  const mimeType = entry.response.content.mimeType?.toLowerCase() || "";

  if (mimeType.includes("text/html") || url.endsWith(".html")) return "document";
  if (
    mimeType.includes("javascript") ||
    url.includes(".js") ||
    url.includes(".ts") ||
    url.includes(".jsx") ||
    url.includes(".tsx")
  )
    return "script";
  if (
    mimeType.includes("text/css") ||
    url.includes(".css") ||
    url.includes(".scss") ||
    url.includes(".sass")
  )
    return "stylesheet";
  if (mimeType.startsWith("image/") || url.match(/\.(jpg|jpeg|png|gif|svg|webp|ico)(\?|$)/))
    return "image";
  if (mimeType.includes("font") || url.match(/\.(woff|woff2|ttf|otf|eot)(\?|$)/)) return "font";
  if (
    mimeType.startsWith("video/") ||
    mimeType.startsWith("audio/") ||
    url.match(/\.(mp4|mp3|avi|mov|wav|ogg)(\?|$)/)
  )
    return "media";
  if (
    entry.request.method !== "GET" ||
    mimeType.includes("json") ||
    mimeType.includes("xml") ||
    url.includes("/api/") ||
    url.includes("/v1/")
  )
    return "xhr";

  return "other";
};

const REQUEST_TYPE_LABELS: Record<RequestType, string> = {
  all: "All",
  document: "Doc",
  script: "JS",
  stylesheet: "CSS",
  image: "Img",
  xhr: "Fetch/XHR",
  font: "Font",
  media: "Media",
  other: "Other",
};

const WaterfallChart: React.FC<{ entry: HarEntry }> = ({ entry }) => {
  const totalTime = entry.time;
  const timings = entry.timings;

  const phases = [
    { name: "DNS", time: timings.dns, color: "#4CAF50" },
    { name: "Connect", time: timings.connect, color: "#FF9800" },
    { name: "SSL", time: timings.ssl || 0, color: "#9C27B0" },
    { name: "Send", time: timings.send, color: "#2196F3" },
    { name: "Wait", time: timings.wait, color: "#F44336" },
    { name: "Receive", time: timings.receive, color: "#607D8B" },
  ].filter((phase) => phase.time > 0);

  // Calculate cumulative times for stacked layout
  let cumulativeTime = 0;
  const stackedPhases = phases.map((phase) => {
    const start = cumulativeTime;
    cumulativeTime += phase.time;
    return {
      ...phase,
      startPercent: (start / totalTime) * 100,
      widthPercent: (phase.time / totalTime) * 100,
    };
  });

  return (
    <Box sx={{ width: "100%", mb: 2 }}>
      {stackedPhases.map((phase) => (
        <Box key={phase.name} sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Typography
            variant="caption"
            sx={{
              minWidth: "70px",
              fontSize: "11px",
              fontWeight: "bold",
              color: "text.secondary",
            }}
          >
            {phase.name}:
          </Typography>
          <Box
            sx={{
              flex: 1,
              mx: 2,
              position: "relative",
              height: "16px",
              backgroundColor: STATUS_COLORS.default.bg,
              borderRadius: "2px",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                left: `${phase.startPercent}%`,
                width: `${Math.max(phase.widthPercent, 2)}%`,
                height: "100%",
                backgroundColor: phase.color,
                borderRadius: "2px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "9px",
                color: "white",
                fontWeight: "bold",
              }}
            >
              {phase.time > 15 ? phase.name.charAt(0) : ""}
            </Box>
          </Box>
          <Typography
            variant="caption"
            sx={{
              minWidth: "50px",
              fontSize: "10px",
              textAlign: "right",
              color: "text.secondary",
            }}
          >
            {phase.time.toFixed(1)}ms
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const ResponseBodyEditor: React.FC<{ entry: HarEntry }> = ({ entry }) => {
  const [showFull, setShowFull] = useState(false);
  const responseBody = entry.response.content?.text || "";

  // Get content type from response headers
  const contentTypeHeader = entry.response.headers.find(
    (h) => h.name.toLowerCase() === "content-type",
  );
  const contentType = contentTypeHeader?.value.toLowerCase() || "";

  // Performance thresholds
  const MAX_SIZE = 100 * 1024; // 100KB
  const PREVIEW_SIZE = 5000; // 5KB for preview
  const isLarge = responseBody.length > MAX_SIZE;
  const isMedium = responseBody.length > PREVIEW_SIZE;

  // Determine the format and apply formatting
  const formatResponseBody = () => {
    if (!responseBody) {
      return {
        content: "No response body",
        language: "plaintext",
        type: "text",
      };
    }

    // Check for media content first
    if (contentType.startsWith("image/")) {
      return {
        content: responseBody,
        language: "plaintext",
        type: "image",
        mimeType: contentType,
      };
    }

    if (contentType.startsWith("video/")) {
      return {
        content: responseBody,
        language: "plaintext",
        type: "video",
        mimeType: contentType,
      };
    }

    if (contentType.startsWith("audio/")) {
      return {
        content: responseBody,
        language: "plaintext",
        type: "audio",
        mimeType: contentType,
      };
    }

    // For large files, only show preview unless explicitly requested
    let bodyToFormat = responseBody;
    if (isLarge && !showFull) {
      bodyToFormat = `${responseBody.substring(0, PREVIEW_SIZE)}\n\n... (truncated)`;
    } else if (isMedium && !showFull && !isLarge) {
      bodyToFormat = `${responseBody.substring(0, PREVIEW_SIZE)}\n\n... (truncated)`;
    }

    try {
      // Check for JSON content
      if (contentType.includes("application/json") || contentType.includes("text/json")) {
        if (showFull || !isMedium) {
          const parsed = JSON.parse(responseBody);
          return {
            content: JSON.stringify(parsed, null, 2),
            language: "json",
            type: "text",
          };
        } else {
          // For preview, try to parse truncated content or show raw
          try {
            const parsed = JSON.parse(bodyToFormat.replace(/\n\n\.\.\. \(truncated\)$/, ""));
            return {
              content: `${JSON.stringify(parsed, null, 2)}\n\n... (truncated)`,
              language: "json",
              type: "text",
            };
          } catch {
            return { content: bodyToFormat, language: "json", type: "text" };
          }
        }
      }

      // For other content types, use the formatted body
      if (
        contentType.includes("text/html") ||
        responseBody.trim().startsWith("<!DOCTYPE") ||
        responseBody.trim().startsWith("<html")
      ) {
        return { content: bodyToFormat, language: "html", type: "text" };
      }

      if (contentType.includes("xml") || responseBody.trim().startsWith("<?xml")) {
        return { content: bodyToFormat, language: "xml", type: "text" };
      }

      if (contentType.includes("text/css")) {
        return { content: bodyToFormat, language: "css", type: "text" };
      }

      if (contentType.includes("javascript") || contentType.includes("application/javascript")) {
        return { content: bodyToFormat, language: "javascript", type: "text" };
      }

      // Try to parse as JSON even if content-type is not set correctly
      if (responseBody.trim().startsWith("{") || responseBody.trim().startsWith("[")) {
        try {
          if (showFull || !isMedium) {
            const parsed = JSON.parse(responseBody);
            return {
              content: JSON.stringify(parsed, null, 2),
              language: "json",
              type: "text",
            };
          } else {
            return { content: bodyToFormat, language: "json", type: "text" };
          }
        } catch {
          // If JSON parsing fails, treat as text
        }
      }
    } catch {
      // If any parsing fails, fall back to original content
    }

    // Default: treat as plain text
    return { content: bodyToFormat, language: "plaintext", type: "text" };
  };

  const { content, language, type, mimeType } = formatResponseBody();

  // For very large files, don't render editor at all initially
  if (isLarge && !showFull) {
    return (
      <Paper variant="outlined" sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6" color="warning.main" gutterBottom>
          Large Response ({(responseBody.length / 1024).toFixed(1)}KB)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This response is too large to display efficiently. You can view a preview or the full
          content.
        </Typography>
        <Stack direction="row" spacing={2} justifyContent="center">
          <Button
            variant="outlined"
            size="small"
            onClick={() => setShowFull(false)}
            disabled={!showFull}
          >
            Show Preview ({(PREVIEW_SIZE / 1024).toFixed(1)}KB)
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => setShowFull(true)}
            color="warning"
          >
            Load Full Content ({(responseBody.length / 1024).toFixed(1)}KB)
          </Button>
        </Stack>
        {showFull && (
          <Box sx={{ mt: 2, height: "400px" }}>
            {type === "image" ? (
              <Box
                sx={{
                  p: 2,
                  textAlign: "center",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Image Preview ({mimeType})
                </Typography>
                <Box
                  sx={{
                    maxWidth: "100%",
                    maxHeight: "320px",
                    overflow: "auto",
                  }}
                >
                  <img
                    src={`data:${mimeType};base64,${content}`}
                    alt="Response content"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                      border: "1px solid #e0e0e0",
                      borderRadius: "4px",
                    }}
                  />
                </Box>
              </Box>
            ) : type === "video" ? (
              <Box
                sx={{
                  p: 2,
                  textAlign: "center",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Video Preview ({mimeType})
                </Typography>
                {/* biome-ignore lint/a11y/useMediaCaption: Dynamic content without captions */}
                <video controls style={{ maxWidth: "100%", maxHeight: "320px" }}>
                  <source src={`data:${mimeType};base64,${content}`} type={mimeType} />
                </video>
              </Box>
            ) : type === "audio" ? (
              <Box
                sx={{
                  p: 2,
                  textAlign: "center",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Audio Preview ({mimeType})
                </Typography>
                {/* biome-ignore lint/a11y/useMediaCaption: Dynamic content without captions */}
                <audio controls style={{ width: "100%", maxWidth: "400px" }}>
                  <source src={`data:${mimeType};base64,${content}`} type={mimeType} />
                </audio>
              </Box>
            ) : (
              <Editor
                height="400px"
                language={language}
                value={content}
                options={{
                  readOnly: true,
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  fontSize: 11,
                  lineHeight: 16,
                  wordWrap: "on",
                  automaticLayout: true,
                  contextmenu: false,
                  selectOnLineNumbers: false,
                  glyphMargin: false,
                  folding: true,
                  lineNumbers: "on",
                  lineDecorationsWidth: 0,
                  lineNumbersMinChars: 3,
                  scrollbar: {
                    vertical: "visible",
                    horizontal: "visible",
                    verticalScrollbarSize: 8,
                    horizontalScrollbarSize: 8,
                  },
                }}
                theme="vs-light"
              />
            )}
          </Box>
        )}
      </Paper>
    );
  }

  // For medium-sized files, show with preview option
  return (
    <Paper variant="outlined" sx={{ overflow: "hidden" }}>
      {isMedium && (
        <Box
          sx={{
            p: 1,
            backgroundColor: STATUS_COLORS.default.bg,
            borderBottom: "1px solid",
            borderBottomColor: "divider",
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">
              Size: {(responseBody.length / 1024).toFixed(1)}KB
              {!showFull && " (showing preview)"}
            </Typography>
            <Button size="small" variant="text" onClick={() => setShowFull(!showFull)}>
              {showFull ? "Show Preview" : "Show Full"}
            </Button>
          </Stack>
        </Box>
      )}
      <Box sx={{ height: "450px" }}>
        {type === "image" ? (
          <Box
            sx={{
              p: 2,
              textAlign: "center",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Image Preview ({mimeType})
            </Typography>
            <Box sx={{ maxWidth: "100%", maxHeight: "380px", overflow: "auto" }}>
              <img
                src={`data:${mimeType};base64,${content}`}
                alt="Response content"
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                  border: "1px solid #e0e0e0",
                  borderRadius: "4px",
                }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = "none";
                  const parent = target.parentElement;
                  if (parent) {
                    parent.innerHTML =
                      '<div style="color: #666; padding: 20px;">Unable to display image preview</div>';
                  }
                }}
              />
            </Box>
          </Box>
        ) : type === "video" ? (
          <Box
            sx={{
              p: 2,
              textAlign: "center",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Video Preview ({mimeType})
            </Typography>
            <Box sx={{ maxWidth: "100%", maxHeight: "380px" }}>
              {/* biome-ignore lint/a11y/useMediaCaption: Dynamic content without captions */}
              <video
                controls
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  border: "1px solid #e0e0e0",
                  borderRadius: "4px",
                }}
                onError={(e) => {
                  const target = e.target as HTMLVideoElement;
                  target.style.display = "none";
                  const parent = target.parentElement;
                  if (parent) {
                    parent.innerHTML =
                      '<div style="color: #666; padding: 20px;">Unable to display video preview</div>';
                  }
                }}
              >
                <source src={`data:${mimeType};base64,${content}`} type={mimeType} />
                Your browser does not support the video element.
              </video>
            </Box>
          </Box>
        ) : type === "audio" ? (
          <Box
            sx={{
              p: 2,
              textAlign: "center",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Audio Preview ({mimeType})
            </Typography>
            {/* biome-ignore lint/a11y/useMediaCaption: Dynamic content without captions */}
            <audio
              controls
              style={{
                width: "100%",
                maxWidth: "400px",
              }}
              onError={(e) => {
                const target = e.target as HTMLAudioElement;
                target.style.display = "none";
                const parent = target.parentElement;
                if (parent) {
                  parent.innerHTML =
                    '<div style="color: #666; padding: 20px;">Unable to display audio preview</div>';
                }
              }}
            >
              <source src={`data:${mimeType};base64,${content}`} type={mimeType} />
              Your browser does not support the audio element.
            </audio>
          </Box>
        ) : (
          <Editor
            height="450px"
            language={language}
            value={content}
            options={{
              readOnly: true,
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
              fontSize: 12,
              lineHeight: 18,
              wordWrap: "on",
              automaticLayout: true,
              contextmenu: false,
              selectOnLineNumbers: false,
              glyphMargin: false,
              folding: true,
              lineNumbers: "on",
              lineDecorationsWidth: 0,
              lineNumbersMinChars: 3,
              scrollbar: {
                vertical: "visible",
                horizontal: "visible",
                verticalScrollbarSize: 10,
                horizontalScrollbarSize: 10,
              },
            }}
            theme="vs-light"
          />
        )}
      </Box>
    </Paper>
  );
};

const RequestBodyEditor: React.FC<{ entry: HarEntry }> = ({ entry }) => {
  const [showFull, setShowFull] = useState(false);
  const requestBody = entry.request.postData?.text || "";

  // Get content type from request headers or postData
  const contentTypeHeader = entry.request.headers.find(
    (h) => h.name.toLowerCase() === "content-type",
  );
  const contentType =
    contentTypeHeader?.value.toLowerCase() || entry.request.postData?.mimeType?.toLowerCase() || "";

  // Performance thresholds
  const MAX_SIZE = 100 * 1024; // 100KB
  const PREVIEW_SIZE = 5000; // 5KB for preview
  const isLarge = requestBody.length > MAX_SIZE;
  const isMedium = requestBody.length > PREVIEW_SIZE;

  // Determine the format and apply formatting
  const formatRequestBody = () => {
    if (!requestBody) {
      return { content: "No request body", language: "plaintext" };
    }

    // For large files, only show preview unless explicitly requested
    let bodyToFormat = requestBody;
    if (isLarge && !showFull) {
      bodyToFormat = `${requestBody.substring(0, PREVIEW_SIZE)}\n\n... (truncated)`;
    } else if (isMedium && !showFull && !isLarge) {
      bodyToFormat = `${requestBody.substring(0, PREVIEW_SIZE)}\n\n... (truncated)`;
    }

    try {
      // Check for JSON content
      if (contentType.includes("application/json") || contentType.includes("text/json")) {
        if (showFull || !isMedium) {
          const parsed = JSON.parse(requestBody);
          return {
            content: JSON.stringify(parsed, null, 2),
            language: "json",
          };
        } else {
          try {
            const parsed = JSON.parse(bodyToFormat.replace(/\n\n\.\.\. \(truncated\)$/, ""));
            return {
              content: `${JSON.stringify(parsed, null, 2)}\n\n... (truncated)`,
              language: "json",
            };
          } catch {
            return { content: bodyToFormat, language: "json" };
          }
        }
      }

      // Check for form data
      if (contentType.includes("application/x-www-form-urlencoded")) {
        const sourceData = showFull ? requestBody : bodyToFormat;
        const formatted = sourceData
          .split("&")
          .map((param) => {
            try {
              return decodeURIComponent(param);
            } catch {
              return param;
            }
          })
          .join("\n");
        return { content: formatted, language: "plaintext" };
      }

      // Check for XML content
      if (contentType.includes("xml") || requestBody.trim().startsWith("<?xml")) {
        return { content: bodyToFormat, language: "xml" };
      }

      // Try to parse as JSON even if content-type is not set correctly
      if (requestBody.trim().startsWith("{") || requestBody.trim().startsWith("[")) {
        try {
          if (showFull || !isMedium) {
            const parsed = JSON.parse(requestBody);
            return {
              content: JSON.stringify(parsed, null, 2),
              language: "json",
            };
          } else {
            return { content: bodyToFormat, language: "json" };
          }
        } catch {
          // If JSON parsing fails, treat as text
        }
      }
    } catch {
      // If any parsing fails, fall back to original content
    }

    // Default: treat as plain text
    return { content: bodyToFormat, language: "plaintext" };
  };

  const { content, language } = formatRequestBody();

  // For very large files, don't render editor at all initially
  if (isLarge && !showFull) {
    return (
      <Paper variant="outlined" sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6" color="warning.main" gutterBottom>
          Large Request ({(requestBody.length / 1024).toFixed(1)}KB)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This request body is too large to display efficiently. You can view a preview or the full
          content.
        </Typography>
        <Stack direction="row" spacing={2} justifyContent="center">
          <Button
            variant="outlined"
            size="small"
            onClick={() => setShowFull(false)}
            disabled={!showFull}
          >
            Show Preview ({(PREVIEW_SIZE / 1024).toFixed(1)}KB)
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => setShowFull(true)}
            color="warning"
          >
            Load Full Content ({(requestBody.length / 1024).toFixed(1)}KB)
          </Button>
        </Stack>
        {showFull && (
          <Box sx={{ mt: 2, height: "400px" }}>
            <Editor
              height="400px"
              language={language}
              value={content}
              options={{
                readOnly: true,
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
                fontSize: 11,
                lineHeight: 16,
                wordWrap: "on",
                automaticLayout: true,
                contextmenu: false,
                selectOnLineNumbers: false,
                glyphMargin: false,
                folding: true,
                lineNumbers: "on",
                lineDecorationsWidth: 0,
                lineNumbersMinChars: 3,
                scrollbar: {
                  vertical: "visible",
                  horizontal: "visible",
                  verticalScrollbarSize: 8,
                  horizontalScrollbarSize: 8,
                },
              }}
              theme="vs-light"
            />
          </Box>
        )}
      </Paper>
    );
  }

  // For medium-sized files, show with preview option
  return (
    <Paper variant="outlined" sx={{ overflow: "hidden" }}>
      {isMedium && (
        <Box
          sx={{
            p: 1,
            backgroundColor: STATUS_COLORS.default.bg,
            borderBottom: "1px solid",
            borderBottomColor: "divider",
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">
              Size: {(requestBody.length / 1024).toFixed(1)}KB
              {!showFull && " (showing preview)"}
            </Typography>
            <Button size="small" variant="text" onClick={() => setShowFull(!showFull)}>
              {showFull ? "Show Preview" : "Show Full"}
            </Button>
          </Stack>
        </Box>
      )}
      <Box sx={{ height: "450px" }}>
        <Editor
          height="450px"
          language={language}
          value={content}
          options={{
            readOnly: true,
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            fontSize: 12,
            lineHeight: 18,
            wordWrap: "on",
            automaticLayout: true,
            contextmenu: false,
            selectOnLineNumbers: false,
            glyphMargin: false,
            folding: true,
            lineNumbers: "on",
            lineDecorationsWidth: 0,
            lineNumbersMinChars: 3,
            scrollbar: {
              vertical: "visible",
              horizontal: "visible",
              verticalScrollbarSize: 10,
              horizontalScrollbarSize: 10,
            },
          }}
          theme="vs-light"
        />
      </Box>
    </Paper>
  );
};

const DetailsPanel: React.FC<DetailsPanelProps> = ({
  entry,
  open,
  onClose,
  testId,
  testRunId,
  appConfigId,
}) => {
  const [selectedTab, setSelectedTab] = useState(0);

  if (!entry) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        zIndex: 1400, // Above Dialog (1300)
        "& .MuiDrawer-paper": {
          width: "40%",
          minWidth: "400px",
          pt: "64px", // Add top padding to account for app header
        },
      }}
    >
      <Box sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6" noWrap>
            {entry.request.method} {entry.request.url.split("?")[0]}
          </Typography>
          <IconButton aria-label="Close" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>

        <Stack direction="row" spacing={1} mb={2}>
          <Chip
            label={entry.request.method}
            color={getMethodColor(entry.request.method)}
            size="small"
          />
          <Chip
            label={entry.response.status}
            color={getStatusColor(entry.response.status)}
            size="small"
          />
          <Chip label={`${entry.time.toFixed(0)}ms`} variant="outlined" size="small" />
        </Stack>

        {/* Waterfall timing chart */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Timing Breakdown
          </Typography>
          <WaterfallChart entry={entry} />
        </Box>

        <Tabs value={selectedTab} onChange={(_, v) => setSelectedTab(v)} sx={{ mb: 3 }}>
          <Tab label="Headers" />
          <Tab label="Request" />
          <Tab label="Response" />
          <Tab label="Timing" />
          <Tab label="Create Check" />
        </Tabs>

        <Box sx={{ flex: 1, overflow: "auto" }}>
          {selectedTab === 0 && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Request Headers
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableBody>
                      {entry.request.headers.map((header, i) => (
                        <TableRow key={i}>
                          <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8em" }}>
                            {header.name}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: "monospace",
                              fontSize: "0.8em",
                              wordBreak: "break-all",
                            }}
                          >
                            {header.value}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Response Headers
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableBody>
                      {entry.response.headers.map((header, i) => (
                        <TableRow key={i}>
                          <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8em" }}>
                            {header.name}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: "monospace",
                              fontSize: "0.8em",
                              wordBreak: "break-all",
                            }}
                          >
                            {header.value}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Stack>
          )}
          {selectedTab === 1 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Request Body
              </Typography>
              <RequestBodyEditor entry={entry} />
            </Box>
          )}
          {selectedTab === 2 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Response Body
              </Typography>
              <ResponseBodyEditor entry={entry} />
            </Box>
          )}
          {selectedTab === 3 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Detailed Timing
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableBody>
                    {Object.entries(entry.timings).map(([key, value]) => (
                      <TableRow key={key}>
                        <TableCell sx={{ textTransform: "capitalize" }}>
                          {key.replace(/([A-Z])/g, " $1").toLowerCase()}
                        </TableCell>
                        <TableCell>{value >= 0 ? `${value.toFixed(2)}ms` : "N/A"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
          {selectedTab === 4 && (
            <CreateCheckFromHarPanel
              entry={entry}
              testId={testId}
              testRunId={testRunId}
              appConfigId={appConfigId}
              onClose={onClose}
            />
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

// Fixed column widths for consistent alignment
const COL_PATH = 200;
const COL_METHOD = 70;
const COL_STATUS = 60;
const COL_SIZE = 70;
const COL_TIME = 60;

// Individual row component - extracted for cleaner virtualization
const TimelineRow: React.FC<{
  processed: ProcessedEntry;
  isHighlighted: boolean;
  onEntryClick: (entry: HarEntry) => void;
  onEntryHover: (entry: HarEntry | null) => void;
}> = ({ processed, isHighlighted, onEntryClick, onEntryHover }) => {
  const { entry, urlPath, urlSearch, fileName, leftPercent, widthPercent, tooltipText } = processed;

  return (
    <Box
      title={tooltipText}
      onMouseEnter={() => onEntryHover(entry)}
      onMouseLeave={() => onEntryHover(null)}
      {...clickableProps(() => onEntryClick(entry))}
      onClick={() => onEntryClick(entry)}
      sx={{
        display: "flex",
        alignItems: "center",
        height: "26px",
        borderBottom: "1px solid",
        borderBottomColor: "grey.200",
        cursor: "pointer",
        backgroundColor: isHighlighted ? STATUS_COLORS.info.bg : "transparent",
        "&:hover": { backgroundColor: STATUS_COLORS.default.bg },
      }}
    >
      <Box sx={{ width: COL_PATH, flexShrink: 0, pl: 1, overflow: "hidden" }}>
        <Typography variant="caption" noWrap title={urlPath + urlSearch}>
          {fileName}
        </Typography>
      </Box>
      <Box sx={{ width: COL_METHOD, flexShrink: 0 }}>
        <Chip
          label={entry.request.method}
          color={getMethodColor(entry.request.method)}
          size="small"
          sx={{ fontSize: "9px", height: "18px" }}
        />
      </Box>
      <Box sx={{ width: COL_STATUS, flexShrink: 0 }}>
        <Chip
          label={entry.response.status}
          color={getStatusColor(entry.response.status)}
          size="small"
          sx={{ fontSize: "9px", height: "18px" }}
        />
      </Box>
      <Box sx={{ width: COL_SIZE, flexShrink: 0, textAlign: "right" }}>
        <Typography variant="caption">
          {entry.response.bodySize > 0 ? `${(entry.response.bodySize / 1024).toFixed(1)}KB` : "-"}
        </Typography>
      </Box>
      <Box sx={{ width: COL_TIME, flexShrink: 0, textAlign: "right", pr: 1 }}>
        <Typography variant="caption">{entry.time.toFixed(0)}ms</Typography>
      </Box>
      <Box
        sx={{
          flex: 1,
          minWidth: 100,
          position: "relative",
          height: "12px",
          mx: 1,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: `${leftPercent}%`,
            width: `${Math.min(widthPercent, 100 - leftPercent)}%`,
            height: "10px",
            backgroundColor:
              getStatusColor(entry.response.status) === "success"
                ? STATUS_COLORS.success.border
                : getStatusColor(entry.response.status) === "error"
                  ? STATUS_COLORS.error.border
                  : STATUS_COLORS.warning.border,
            borderRadius: "2px",
            top: "1px",
          }}
        />
      </Box>
    </Box>
  );
};

const TimelineWaterfall: React.FC<{
  processedEntries: ProcessedEntry[];
  onEntryClick: (entry: HarEntry) => void;
  onEntryHover: (entry: HarEntry | null) => void;
  highlightedEntry: HarEntry | null;
}> = ({ processedEntries, onEntryClick, onEntryHover, highlightedEntry }) => {
  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        minHeight: 0,
      }}
    >
      {/* Header row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          height: "30px",
          backgroundColor: STATUS_COLORS.default.bg,
          borderBottom: "1px solid",
          borderBottomColor: "divider",
          flexShrink: 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            width: COL_PATH,
            flexShrink: 0,
            pl: 1,
            fontWeight: "bold",
            color: "text.secondary",
          }}
        >
          Path
        </Typography>
        <Typography
          variant="caption"
          sx={{
            width: COL_METHOD,
            flexShrink: 0,
            fontWeight: "bold",
            color: "text.secondary",
          }}
        >
          Method
        </Typography>
        <Typography
          variant="caption"
          sx={{
            width: COL_STATUS,
            flexShrink: 0,
            fontWeight: "bold",
            color: "text.secondary",
          }}
        >
          Status
        </Typography>
        <Typography
          variant="caption"
          sx={{
            width: COL_SIZE,
            flexShrink: 0,
            fontWeight: "bold",
            color: "text.secondary",
            textAlign: "right",
          }}
        >
          Size
        </Typography>
        <Typography
          variant="caption"
          sx={{
            width: COL_TIME,
            flexShrink: 0,
            fontWeight: "bold",
            color: "text.secondary",
            textAlign: "right",
            pr: 1,
          }}
        >
          Time
        </Typography>
        <Box sx={{ flex: 1, minWidth: 100, pl: 1, pr: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: "bold", color: "text.secondary" }}>
            Waterfall
          </Typography>
        </Box>
      </Box>

      {/* Virtualized entries */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <Virtuoso
          data={processedEntries}
          overscan={20}
          itemContent={(_, processed) => (
            <TimelineRow
              processed={processed}
              isHighlighted={highlightedEntry === processed.entry}
              onEntryClick={onEntryClick}
              onEntryHover={onEntryHover}
            />
          )}
          style={{ height: "100%" }}
        />
      </Box>
    </Box>
  );
};

export const HarViewer: React.FC<HarViewerProps> = ({
  harPath,
  testId,
  testRunId,
  appConfigId,
  showExpandButton = true,
}) => {
  const nav = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  // Deferred search value for better typing responsiveness
  const deferredSearch = useDeferredValue(searchInput);
  const [selectedEntry, setSelectedEntry] = useState<HarEntry | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<RequestType[]>(["all"]);
  const [highlightedEntry, setHighlightedEntry] = useState<HarEntry | null>(null);
  const [expandedModalOpen, setExpandedModalOpen] = useState(false);

  const {
    data: harData,
    isLoading,
    error,
  } = useQuery<HarData, Error>({
    queryKey: ["har-data", harPath],
    queryFn: async () => {
      const response = await fetch(harPath);
      if (!response.ok) {
        throw new Error(`Failed to fetch HAR data: ${response.status} ${response.statusText}`);
      }
      return response.json();
    },
    enabled: !!harPath,
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });

  // HAR analysis mutation
  const { mutate: analyzeHar, isPending: isAnalyzing } = useAnalyzeHar();

  const handleAnalyzeHar = () => {
    if (!testRunId) return;

    analyzeHar(
      {
        include_all_domains: false,
        _url: `/checks/analyze-har/${testRunId}`,
      },
      {
        onSuccess: () => {
          nav("/checks?tab=suggested");
        },
      },
    );
  };

  const filteredEntries = useMemo(() => {
    if (!harData) return [];

    const searchLower = deferredSearch.toLowerCase();
    const filtered = harData.log.entries.filter((entry) => {
      // Text search filter
      const matchesSearch =
        entry.request.url.toLowerCase().includes(searchLower) ||
        entry.request.method.toLowerCase().includes(searchLower) ||
        entry.response.status.toString().includes(deferredSearch);

      // Type filter
      const entryType = getRequestType(entry) as RequestType;
      const matchesType = selectedFilters.includes("all") || selectedFilters.includes(entryType);

      return matchesSearch && matchesType;
    });

    // Sort by start time
    filtered.sort((a, b) => {
      const aTime = new Date(a.startedDateTime).getTime();
      const bTime = new Date(b.startedDateTime).getTime();
      return aTime - bTime;
    });

    return filtered;
  }, [harData, deferredSearch, selectedFilters]);

  // Pre-compute expensive values for virtualized rendering
  const processedEntries = useMemo((): ProcessedEntry[] => {
    if (filteredEntries.length === 0) return [];

    // Calculate timeline bounds once
    const startTime = Math.min(
      ...filteredEntries.map((e) => new Date(e.startedDateTime).getTime()),
    );
    const endTime = Math.max(
      ...filteredEntries.map((e) => new Date(e.startedDateTime).getTime() + e.time),
    );
    const totalDuration = endTime - startTime || 1000; // Avoid division by zero

    return filteredEntries.map((entry, index) => {
      const timestamp = new Date(entry.startedDateTime).getTime();
      let urlPath: string;
      let urlSearch: string;
      let fileName: string;

      try {
        const url = new URL(entry.request.url);
        urlPath = url.pathname;
        urlSearch = url.search;
        fileName = url.pathname.split("/").pop() || url.pathname;
      } catch {
        // Fallback for malformed URLs
        urlPath = entry.request.url;
        urlSearch = "";
        fileName = entry.request.url;
      }

      const leftPercent = Math.max(0, ((timestamp - startTime) / totalDuration) * 100);
      const widthPercent = Math.max(1, (entry.time / totalDuration) * 100);

      const tooltipText = `${entry.request.method} ${urlPath}${urlSearch}\nStatus: ${entry.response.status}\nTime: ${entry.time.toFixed(0)}ms\nSize: ${entry.response.bodySize > 0 ? `${(entry.response.bodySize / 1024).toFixed(1)}KB` : "-"}`;

      return {
        entry,
        index,
        timestamp,
        urlPath,
        urlSearch,
        fileName,
        leftPercent,
        widthPercent,
        tooltipText,
      };
    });
  }, [filteredEntries]);

  const handleEntryClick = useCallback((entry: HarEntry) => {
    setSelectedEntry(entry);
    setDetailsOpen(true);
  }, []);

  const handleEntryHover = useCallback((entry: HarEntry | null) => {
    setHighlightedEntry(entry);
  }, []);

  const handleDetailsClose = () => {
    setDetailsOpen(false);
    setSelectedEntry(null);
  };

  const handleFilterChange = (_event: React.MouseEvent<HTMLElement>, newFilters: RequestType[]) => {
    // If nothing is selected, default to "all"
    if (newFilters.length === 0) {
      setSelectedFilters(["all"]);
      return;
    }

    // If "all" was clicked and other filters were selected, select only "all"
    if (newFilters.includes("all") && !selectedFilters.includes("all")) {
      setSelectedFilters(["all"]);
      return;
    }

    // If "all" was already selected and a specific filter is clicked, deselect "all" and select the specific filter
    if (selectedFilters.includes("all") && newFilters.length > 1) {
      const nonAllFilters = newFilters.filter((f) => f !== "all");
      setSelectedFilters(nonAllFilters);
      return;
    }

    // Otherwise, use the new filters as-is (but remove "all" if other filters are present)
    if (newFilters.includes("all") && newFilters.length > 1) {
      setSelectedFilters(newFilters.filter((f) => f !== "all"));
    } else {
      setSelectedFilters(newFilters);
    }
  };

  const availableTypes = useMemo(() => {
    if (!harData) return [];

    const typeCounts: Record<string, number> = {};
    harData.log.entries.forEach((entry) => {
      const type = getRequestType(entry);
      typeCounts[type] = (typeCounts[type] || 0) + 1;
    });

    return Object.keys(typeCounts).filter((type) => typeCounts[type] > 0);
  }, [harData]);

  if (isLoading) {
    return <LoadingBox />;
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to load HAR data: {error.message || String(error)}
      </Alert>
    );
  }

  if (!harData) {
    return <EmptyState message="No HAR data available" />;
  }

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Search bar with expand button */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexShrink: 0 }}>
        <TextField
          label="Search requests"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          size="small"
          fullWidth
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: "action.active" }} />,
          }}
        />
        {showExpandButton && (
          <Tooltip title="Expand view">
            <IconButton
              aria-label="Expand view"
              size="small"
              onClick={() => setExpandedModalOpen(true)}
            >
              <OpenInFullIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      {/* Expanded Modal */}
      {showExpandButton && (
        <Dialog
          open={expandedModalOpen}
          onClose={() => setExpandedModalOpen(false)}
          maxWidth="xl"
          fullWidth
          PaperProps={{
            sx: { height: "85vh", maxHeight: "85vh" },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h6">Network Activity</Typography>
            <IconButton aria-label="Close" onClick={() => setExpandedModalOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 2, height: "calc(100% - 64px)" }}>
            <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
              {/* Search bar inside modal */}
              <Box sx={{ mt: 1, mb: 1 }}>
                <TextField
                  label="Search requests"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  size="small"
                  fullWidth
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: "action.active" }} />,
                  }}
                />
              </Box>

              {/* Filter controls inside modal */}
              <Box
                sx={{
                  pb: 1,
                  mb: 2,
                  borderBottom: "1px solid",
                  borderBottomColor: "divider",
                  backgroundColor: "background.default",
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <ToggleButtonGroup
                    value={selectedFilters}
                    onChange={handleFilterChange}
                    aria-label="request type filters"
                    size="small"
                  >
                    <ToggleButton value="all" aria-label="all requests">
                      {REQUEST_TYPE_LABELS.all}
                    </ToggleButton>
                    {availableTypes.map((type) => (
                      <ToggleButton key={type} value={type} aria-label={`${type} requests`}>
                        {REQUEST_TYPE_LABELS[type as RequestType] || type}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                  <Typography variant="body2" color="text.secondary">
                    {filteredEntries.length} requests
                  </Typography>
                </Stack>
              </Box>

              {/* Timeline waterfall inside modal */}
              <Box sx={{ flex: 1, overflow: "hidden" }}>
                <TimelineWaterfall
                  processedEntries={processedEntries}
                  onEntryClick={handleEntryClick}
                  onEntryHover={handleEntryHover}
                  highlightedEntry={highlightedEntry}
                />
              </Box>
            </Box>
          </DialogContent>
        </Dialog>
      )}

      {/* Filter controls - moved up */}
      <Box
        sx={{
          pb: 1,
          mb: 1,
          borderBottom: "1px solid",
          borderBottomColor: "divider",
          backgroundColor: "background.default",
          flexShrink: 0,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <ToggleButtonGroup
            value={selectedFilters}
            onChange={handleFilterChange}
            aria-label="request type filters"
            size="small"
          >
            <ToggleButton value="all" aria-label="all requests">
              {REQUEST_TYPE_LABELS.all}
            </ToggleButton>
            {availableTypes.map((type) => (
              <ToggleButton key={type} value={type} aria-label={`${type} requests`}>
                {REQUEST_TYPE_LABELS[type as RequestType] || type}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Stack direction="row" spacing={2} alignItems="center">
            {testRunId && (
              <Tooltip title="Analyze network traffic and suggest API checks">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AutoFixHighIcon />}
                  onClick={handleAnalyzeHar}
                  disabled={isAnalyzing}
                >
                  {isAnalyzing ? "Analyzing..." : "Analyze for Checks"}
                </Button>
              </Tooltip>
            )}
            <Typography variant="body2" color="text.secondary">
              {filteredEntries.length} requests
            </Typography>
          </Stack>
        </Stack>
      </Box>

      {/* Timeline waterfall */}
      <Box sx={{ flex: 1, overflow: "hidden", minHeight: 0 }}>
        <TimelineWaterfall
          processedEntries={processedEntries}
          onEntryClick={handleEntryClick}
          onEntryHover={handleEntryHover}
          highlightedEntry={highlightedEntry}
        />
      </Box>

      <DetailsPanel
        entry={selectedEntry}
        open={detailsOpen}
        onClose={handleDetailsClose}
        testId={testId}
        testRunId={testRunId}
        appConfigId={appConfigId}
      />
    </Box>
  );
};
